import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Props {
  audience: 'agent' | 'contact';
}

const apiBase = (audience: 'agent' | 'contact') =>
  audience === 'agent' ? '/api/auth/agent/reset' : '/api/portal/auth/reset';

export function PasswordResetPage({ audience }: Props): React.ReactElement {
  useDocumentTitle('Reset password');
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'done'>('loading');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ ok: true; email: string }>(`${apiBase(audience)}/check/${encodeURIComponent(token)}`)
      .then((r) => {
        setEmail(r.email);
        setState('ready');
      })
      .catch(() => setState('invalid'));
  }, [audience, token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (pw !== pw2) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`${apiBase(audience)}/complete`, {
        method: 'POST',
        json: { token, newPassword: pw },
      });
      setState('done');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  }

  if (state === 'loading') return <p className="text-[var(--color-muted)]">Loading…</p>;

  if (state === 'invalid') {
    return (
      <div className="mx-auto max-w-md">
        <Card>
          <h1 className="text-xl font-bold text-[var(--color-header)]">Reset link unavailable</h1>
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            This password reset link is invalid or has expired (links last 1 hour and can only be
            used once).
          </p>
          <p className="mt-3 text-sm">
            <Link to="/login" className="text-[var(--color-t3-blue)] hover:underline">
              Request a new one
            </Link>
          </p>
        </Card>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="mx-auto max-w-md">
        <Card>
          <h1 className="text-xl font-bold text-[var(--color-header)]">Password updated</h1>
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            Your password has been changed. Sign in with your new password and your existing
            authenticator code.
          </p>
          <div className="mt-3">
            <Button onClick={() => navigate('/login', { replace: true })}>Go to sign in</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <Card>
        <h1 className="text-xl font-bold text-[var(--color-header)]">Reset your password</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Resetting password for <strong>{email}</strong>. Your TOTP code stays the same.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <FormField
            label="New password"
            name="new"
            type="password"
            autoComplete="new-password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            hint="At least 12 characters with upper, lower, and a number."
          />
          <FormField
            label="Confirm new password"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
          />
          {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Updating…' : 'Set new password'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
